import styles from './AssetHealthCard.module.scss';
import { AssetHealth } from '@customer/store/apis/healthApi';
import { useTranslation } from 'react-i18next';
import { VdsButton, VdsCard, VdsIcon, VdsTooltip } from '@volt/volt-extended-components-react';
import AssetWidget from '@customer/components/asset-data/asset-widget/AssetWidget';
import { MouseEvent as ReactMouseEvent, useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { useAppDispatch } from '@customer/store/hooks';
import { AssetSidePanelTab, openSidePanel, SidePanel } from '@customer/store/slices/globalSlice';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import { SystemTypePpu } from '@customer/store/apis/assetsApi';
import { useNavigate } from 'react-router-dom';

export interface AssetHealthCardProps {
  asset: AssetHealth;
  faultLevel: number;
}

const levelColours = [
  'var(--static-red-500)',
  'var(--static-orange-500)',
  'var(--static-yellow-500)',
];

export function AssetHealthCard({ asset, faultLevel }: AssetHealthCardProps) {
  const { t } = useTranslation();
  const { showDateOnly, showTimeOnly, getNumberPercentText } = useLocaleFormatter();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const isPpu = asset.hw === SystemTypePpu;

  const onClicked = useCallback(() => {
    dispatch(
      openSidePanel({
        sidePanel: SidePanel.Asset,
        modelId: asset.id.toString(),
        // Open the side panel asset config tab if dealing with PPU
        tab: isPpu ? AssetSidePanelTab.Configuration : AssetSidePanelTab.SelfTestSummary,
        // Only open the fault level accordion for non-PPU
        defaultAccordion: isPpu ? undefined : faultLevel.toString(),
      }),
    );
  }, [dispatch, asset.id, isPpu, faultLevel]);

  const onClickAssetPage = useCallback(
    (e: ReactMouseEvent) => {
      // Prevent the button click from bubbling to the parent VdsCard.
      e.stopPropagation();
      const tabSlug = isPpu ? 'details' : 'self-test';
      navigate(`/asset/${asset.id}/${tabSlug}`);
    },
    [navigate, asset.id, isPpu],
  );

  const faultLevelIndex = faultLevel - 1;

  const color = levelColours[faultLevelIndex];

  const icon = useMemo(() => {
    return asset.stsCfg ? (
      <VdsIcon size="32px" svgIcon={'good'} color={'green-500'}></VdsIcon>
    ) : (
      <VdsIcon size="32px" svgIcon={'caution'} color={'red-500'}></VdsIcon>
    );
  }, [asset.stsCfg]);

  const reportTime = useMemo<DateTime | null>(
    () => (asset.dtRep ? DateTime.fromISO(asset.dtRep) : null),
    [asset.dtRep],
  );

  const percentageWidth = Math.min(asset.levelPercentages[faultLevelIndex] * 150, 150);

  return (
    <VdsCard className={styles.card} padding="spacing-04" button>
      <div className={styles.container} onClick={onClicked}>
        {/* Name */}
        <section className={`${styles.section} ${styles['section--1']}`}>
          <div className={styles.asset}>
            <div className={styles.assetName}>
              <div
                className={`${styles.assetName__text} ${!asset.stsNam && styles['assetName__text--bad']}`}
              >
                {asset.displayName}
              </div>
              {!asset.stsNam && (
                <VdsTooltip
                  id={`asset-health-card-name-mismatch-${asset.id}`}
                  event="hover"
                  max-width="150px"
                  position="bottom"
                  type="standard"
                  text={t('AssetNameMismatch')}
                >
                  <VdsIcon
                    slot="tooltip-trigger"
                    color="red-500"
                    svgIcon="information"
                    size="24px"
                  />
                </VdsTooltip>
              )}
            </div>
            <div className={styles.deviceType}>{asset.displayHw}</div>
          </div>

          <AssetWidget title={t('IgnitionOnTime')}>
            <div className={styles.value__primary}>{t('Days', { count: asset.ignDays })}</div>
            <div className={styles.value__secondary}>{asset.ignDuration}</div>
          </AssetWidget>
        </section>

        <div className={styles.divider} />

        {/* Diagnostics */}
        <section className={`${styles.section} ${styles['section--2']}`}>
          <AssetWidget title={t('TimeInLFault', { level: faultLevel })}>
            <div>{getNumberPercentText(asset.levelPercentages[faultLevelIndex])}</div>
            <div
              className={styles.value__percentbar}
              style={{ width: `${percentageWidth}px`, background: `${color}` }}
            ></div>
          </AssetWidget>

          <AssetWidget title={t('ActiveTotalFaults')}>
            <span
              className={`${styles.activeFaults} ${asset.fa[faultLevelIndex] === 0 && styles['activeFaults--none']}`}
            >
              {asset.fa[faultLevelIndex]}
            </span>
            <span className={styles.separator}> / </span>
            {asset.fc[faultLevelIndex]}
          </AssetWidget>
        </section>

        <div className={styles.divider} />

        {/* Configuration */}
        <section className={`${styles.section} ${styles['section--3']}`}>
          <AssetWidget title={t('SoftwareStatus')}>{icon}</AssetWidget>

          <AssetWidget title={t('LastReportedTitleCase')}>
            {reportTime ? (
              <>
                <div className={styles.value__primary}>{showDateOnly(reportTime)}</div>
                <div className={styles.value__secondary}>{showTimeOnly(reportTime, true)}</div>
              </>
            ) : (
              <div className={styles.value__primary}>{t('NotReported')}</div>
            )}
          </AssetWidget>
        </section>

        <div className={styles.divider} />

        {/* Button */}
        <div className={styles.buttonblock}>
          <VdsButton variant="secondary" onClick={onClickAssetPage}>
            {/* Note: Do not specify an icon slot or VdsButton will add a margin to the icon. */}
            <VdsIcon svgIcon="asset" size="1rem" />
          </VdsButton>
        </div>
      </div>
    </VdsCard>
  );
}

export default AssetHealthCard;
